// Hostname gate for WHITELABEL-1 layer 4 (shell boot).
//
// Mirrors `isDemoHost()` (demoHost.js) — a pure, dependency-free hostname
// classifier the shell can call at module-load time, before anything else
// has mounted. `isPlatformHost` below is the CLIENT-SIDE twin of the
// server's `isPlatformHost` (server/src/core/services/CustomDomainService.js)
// — same three suffixes, same "empty/SSR host → treat as platform" fail-safe.
// It is NOT a re-derivation of the server's actual host→scope MAPPING (which
// org/workspace a given custom domain resolves to) — that stays entirely
// server-side (`resolveScopeForHost`, `customDomainScope` middleware); this
// file only answers "is the current host one of Symbols' own platform hosts,
// or something else" — the same category judgment
// `server 4fbe1ba0`'s `customDomainScope` middleware makes before it will
// even attempt a DB read.
//
// Per CLAUDE.md "never hardcode a domain": the three suffixes below are the
// PLATFORM's own domains (symbols.app / symbo.ls / localhost) — the fixed
// rule every request is classified against, mirrored 1:1 from the server's
// default `PLATFORM_HOST_SUFFIXES`. No customer domain (e.g. an enterprise
// org's `app.<their-domain>`) is ever named here or anywhere in this file.
//
// Local simulation (no DNS / no `customDomains` row needed): set
// `localStorage.symbols_force_custom_domain='1'` (persists across reloads)
// OR append `?customDomain=1` to any URL (one visit) — same override
// convention as `isDemoHost()`'s `symbols_force_demo` / `?demo=1`.
const PLATFORM_HOST_SUFFIXES = ['symbols.app', 'symbo.ls', 'localhost']

// WHITELABEL-DEPLOY-PREVIEW-BASE-1 — the shared preview zone a white-label
// org resolves under (`<slug>.pr.symbols.app`) before it owns a domain of
// its own. Mirrors the server's `WHITELABEL_PREVIEW_BASE` default 1:1 (see
// `server/src/core/services/CustomDomainService.js`) — hardcoded here, not
// env-read, for the same reason `PLATFORM_HOST_SUFFIXES` above is: this
// classifier runs at module-load time, before any build-time env is wired.
const WHITELABEL_PREVIEW_BASE = 'pr.symbols.app'

export const isWhitelabelPreviewHost = (hostname) => {
  const h = typeof hostname === 'string' ? hostname.split(':')[0].trim().toLowerCase() : ''
  if (!h) return false
  return h === WHITELABEL_PREVIEW_BASE || h.endsWith(`.${WHITELABEL_PREVIEW_BASE}`)
}

export const isPlatformHost = (hostname) => {
  const h = typeof hostname === 'string' ? hostname.split(':')[0].trim().toLowerCase() : ''
  if (!h) return true
  if (isWhitelabelPreviewHost(h)) return false
  return PLATFORM_HOST_SUFFIXES.some((suffix) => h === suffix || h.endsWith(`.${suffix}`))
}

export const isCustomDomainHost = () => {
  if (typeof window === 'undefined') return false
  try {
    // Test overrides FIRST — let any host (incl. a real platform host, for
    // local dev) simulate a host-resolved boot without a live DNS + verified
    // `customDomains` row.
    if (window.localStorage.getItem('symbols_force_custom_domain') === '1') return true
    if ((window.location.search || '').includes('customDomain=1')) return true
    return !isPlatformHost(window.location.hostname || '')
  } catch (_) {
    // localStorage / location may throw in sandboxed contexts — fail toward
    // "platform host" so a broken read never accidentally root-maps
    // my.symbols.app.
    return false
  }
}
